import React, { useEffect, useState } from "react";
import {
  Box,
  Typography,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  TableContainer,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Paper,
  CircularProgress,
  MenuItem,
  Select,
  FormControl,
  InputLabel,
  Grid,
  TextareaAutosize,
  Card,
  CardContent,
  TextField,
} from "@mui/material";
import axios from "axios";
import { Slide } from "@mui/material";
import DownloadIcon from "@mui/icons-material/Download";
import SearchIcon from "@mui/icons-material/Search";
import dayjs from "dayjs"; // npm install dayjs
import FilterListIcon from "@mui/icons-material/FilterList";

const statusColors = {
  البداية: "#1976d2",
  "قيد التنفيذ": "#ed6c02",
  منتهية: "#2e7d32",
};

const getCurrentTimestamp = () =>
  new Date().toISOString().replace("T", " ").slice(0, 19);

const EmployeeTickets = () => {
  const user = JSON.parse(localStorage.getItem("data")) || {};
  const myDepartment = user.department2;

  const [allTickets, setAllTickets] = useState([]);
  const [newTicket, setNewTicket] = useState({ title: "", description: "" });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [successDialog, setSuccessDialog] = useState(false);
  const [openTicketDialog, setOpenTicketDialog] = useState(false);
  const [waitingNumber, setWaitingNumber] = useState(null);
  const [openWaitingDialog, setOpenWaitingDialog] = useState(false);
  const [openQueueDialog, setOpenQueueDialog] = useState(false);
  const [file, setFile] = useState(null);
  const [filterStatus, setFilterStatus] = useState("الكل");
  const [filterTitle, setFilterTitle] = useState("الكل");
  const [showFilters, setShowFilters] = useState(false);
  // Extract unique months from tickets for the current department
  const monthsWithTickets = [
    ...new Set(
      allTickets
        .filter((t) => t.department === myDepartment)
        .map((t) => t.created_at.slice(0, 7)) // 'YYYY-MM'
        .filter(Boolean)
    ),
  ].sort((a, b) => b.localeCompare(a)); // Descending (latest first)

  // Update default month when monthsWithTickets changes
  const [filterMonth, setFilterMonth] = useState(
    monthsWithTickets.length > 0 ? monthsWithTickets[0] : ""
  );

  const fetchAllTickets = async () => {
    try {
      const response = await fetch(
        "https://backenderpforseg.samielmetwali.com/tickets/getAllTicketsForAllUsers.php"
      );
      const result = await response.json();
      if (result.success) {
        setAllTickets(result.data);
      }
    } catch (err) {
      console.error("خطأ أثناء جلب التذاكر:", err);
    }
  };

  useEffect(() => {
    fetchAllTickets();
  }, []);

  // احسب رقم الانتظار بعد تحديث كل التذاكر
  useEffect(() => {
    const startedTickets = allTickets
      .filter((t) => t.status === "البداية")
      .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));

    const myTicket = startedTickets.find((t) => t.username === user.name);

    if (myTicket) {
      const position = startedTickets.findIndex((t) => t.id === myTicket.id);
      setWaitingNumber(position + 1);
    } else {
      setWaitingNumber(null);
    }
  }, [allTickets, user.name]);

  // افتح الرسالة فقط بعد معرفة الانتظار الحقيقي
  useEffect(() => {
    if (waitingNumber !== null) {
      setTimeout(() => {
        setOpenWaitingDialog(true);
      }, 500);
    }
  }, [waitingNumber]);

  // احسب رقمي في كل التذاكر (حتى غير "البداية")
  const queue = allTickets
    .filter((ticket) => ticket.status !== "منتهية")
    .sort((a, b) => new Date(a.created_at) - new Date(b.created_at));
  const myIndex = queue.findIndex((t) => t.username === user.name);
  const myQueueNumber = myIndex !== -1 ? myIndex + 1 : null;

  useEffect(() => {
    if (myQueueNumber) {
      setOpenQueueDialog(true);
    }
  }, [myQueueNumber]);

  useEffect(() => {
    setFilterTitle("الكل");
  }, [myDepartment]);

  const handleAddTicket = async () => {
    const hasTitle = newTicket.title.trim() !== "";
    const hasDesc = newTicket.description.trim() !== "";

    setErrors({
      title: !hasTitle,
      description: !hasDesc,
    });

    if (!hasTitle || !hasDesc) return;

    setLoading(true);

    const formData = new FormData();
    formData.append("title", newTicket.title);
    formData.append("description", newTicket.description);
    formData.append("priority", newTicket.priority);
    formData.append("username", user.name);
    formData.append("company", "SEG");
    formData.append("department", user.department2);
    formData.append("status", "البداية");
    formData.append("category", "أخرى");
    formData.append("created_at", getCurrentTimestamp());
    formData.append("assigned_to", "غير مخصص");
    if (file) formData.append("attachment", file);

    try {
      const res = await axios.post(
        "https://backenderpforseg.samielmetwali.com/tickets/add_ticket.php",
        formData
      );
      if (res.data.success) {
        setSuccessDialog(true);
        setOpenTicketDialog(false);
        setNewTicket({ title: "", description: "" });
        setFile(null);

        setTimeout(() => {
          fetchAllTickets(); // جلب التذاكر مجددًا لحساب الدور الصحيح
        }, 1000);
      }
    } catch (err) {
      console.error("فشل إرسال التذكرة:", err);
    } finally {
      setLoading(false);
    }
  };

  const uniqueTitles = [
    "الكل",
    ...Array.from(
      new Set(
        allTickets
          .filter((t) => t.department === myDepartment)
          .map((t) => t.title)
          .filter(Boolean)
      )
    ),
  ];

  const getPriorityByTitle = (title) => {
    switch (title) {
      case "كمبيوتر":
        return "High";
      case "الانترنت":
        return "Critical";
      case "تعيين":
        return "Normal";
      case "صلاحيات":
        return "Low";
      default:
        return "Normal";
    }
  };

  return (
    <Box
      sx={{
        maxWidth: "1200px",
        mx: "auto",
        p: 4,
        direction: "rtl",
        position: "relative",
      }}
    >
      {/* Waiting number card centered */}
      {waitingNumber && waitingNumber > 0 && (
        <Card
          sx={{
            minWidth: 240,
            maxWidth: 340,
            mb: 5,
            mx: "auto",
            p: 3,
            borderRadius: 4,
            boxShadow: "0 8px 32px 0 rgba(0,230,118,0.22)",
            background: "linear-gradient(135deg, #232a34 60%, #232a34 100%)",
            border: "4px solid #00e676",
            color: "#fff",
            textAlign: "center",
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            position: "relative",
          }}
          elevation={12}
        >
          <CardContent sx={{ p: 0 }}>
            <Box sx={{ mb: 1 }}>
              <span role="img" aria-label="queue" style={{ fontSize: 30 }}>
                ⏳
              </span>
            </Box>
            <Typography
              variant="subtitle1"
              sx={{
                fontWeight: "bold",
                mb: 1,
                color: "#00e676",
                letterSpacing: 1,
              }}
            >
              رقم الانتظار
            </Typography>
            <Box
              sx={{
                fontSize: "3.2rem",
                fontWeight: "bold",
                background:
                  "linear-gradient(135deg, #00e676 60%, #00c853 100%)",
                color: "transparent",
                backgroundClip: "text",
                WebkitBackgroundClip: "text",
                WebkitTextFillColor: "transparent",
                borderRadius: "50%",
                width: 90,
                height: 90,
                display: "flex",
                alignItems: "center",
                justifyContent: "center",
                mx: "auto",
                boxShadow: "0 2px 16px 0 rgba(0,230,118,0.22)",
                mb: 1,
                border: "4px solid #00e676",
                position: "relative",
              }}
            >
              {waitingNumber}
            </Box>
            <Typography variant="body1" sx={{ color: "#fff", fontWeight: 600 }}>
              {waitingNumber === 1
                ? "جاء دورك الآن!"
                : `أمامك ${waitingNumber - 1} تذكرة في الانتظار`}
            </Typography>
          </CardContent>
        </Card>
      )}

      <Typography variant="h4" gutterBottom fontWeight="bold">
        نظام التذاكر
      </Typography>

      <Button
        variant="contained"
        sx={{ mb: 2, background: "#00e676", color: "#fff", fontWeight: "bold", '&:hover': { background: '#00c853' } }}
        onClick={() => setOpenTicketDialog(true)}
      >
        + إضافة تذكرة
      </Button>

      {/* Dialog - رقم الانتظار في القسم */}
      <Dialog
        open={openWaitingDialog}
        onClose={() => setOpenWaitingDialog(false)}
        PaperProps={{
          sx: {
            background: "rgba(30, 41, 59, 0.85)", // dark glass
            backdropFilter: "blur(12px)",
            borderRadius: 4,
            border: "2px solid #00e676",
            boxShadow: "0 10px 40px rgba(0, 255, 100, 0.15)",
            minWidth: 340,
            p: 3,
            textAlign: "center",
          },
        }}
      >
        <DialogTitle
          sx={{ fontWeight: "bold", color: "#00e676", fontSize: "1.4rem" }}
        >
          <span role="img" aria-label="queue">
            ⏳
          </span>{" "}
          دورك في قائمة الانتظار
        </DialogTitle>
        <DialogContent>
          {waitingNumber === 1 ? (
            <Typography
              sx={{
                color: "#00e676",
                fontWeight: "bold",
                fontSize: "2rem",
                mb: 2,
              }}
            >
              جاء دورك الآن!
            </Typography>
          ) : (
            <>
              <Typography sx={{ color: "#fff", fontSize: "1.1rem", mb: 1 }}>
                أمامك
              </Typography>
              <Typography
                sx={{
                  color: "#00e676",
                  fontWeight: "bold",
                  fontSize: "3rem",
                  letterSpacing: 2,
                  mb: 1,
                }}
              >
                {waitingNumber - 1}
              </Typography>
              <Typography sx={{ color: "#fff", fontSize: "1.1rem" }}>
                تذكرة في قائمة الانتظار قبل دورك.
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions sx={{ justifyContent: "center" }}>
          <Button
            onClick={() => setOpenWaitingDialog(false)}
            sx={{ background: '#00e676', color: '#fff', fontWeight: 'bold', borderRadius: 2, px: 4, py: 1, boxShadow: 'none', '&:hover': { background: '#00c853' } }}
          >
            إغلاق
          </Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - النجاح */}
      <Dialog open={successDialog} onClose={() => setSuccessDialog(false)}>
        <DialogTitle>✅ تمت الإضافة بنجاح</DialogTitle>
        <DialogActions>
          <Button onClick={() => setSuccessDialog(false)} sx={{ background: '#00e676', color: '#fff', fontWeight: 'bold', '&:hover': { background: '#00c853' } }}>تم</Button>
        </DialogActions>
      </Dialog>

      {/* Dialog - إضافة تذكرة */}
      <Dialog
        open={openTicketDialog}
        onClose={() => setOpenTicketDialog(false)}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        TransitionProps={{ direction: "up" }}
      >
        <DialogTitle>إضافة تذكرة جديدة</DialogTitle>
        <DialogContent>
          <Grid container spacing={2}>
            <Grid item xs={12}>
              <FormControl fullWidth error={errors.title}>
                <InputLabel>العنوان</InputLabel>
                <Select
                  value={newTicket.title}
                  onChange={(e) => {
                    const selectedTitle = e.target.value;
                    setNewTicket((prev) => ({
                      ...prev,
                      title: selectedTitle,
                      priority: getPriorityByTitle(selectedTitle), // هنا التغيير الديناميكي
                    }));
                  }}
                >
                  <MenuItem value="الانترنت">الانترنت</MenuItem>
                  <MenuItem value="تعيين">تعيين</MenuItem>
                  <MenuItem value="كمبيوتر">كمبيوتر</MenuItem>
                  <MenuItem value="صلاحيات">صلاحيات</MenuItem>
                  <MenuItem value="شريحه">شريحة</MenuItem>
                  <MenuItem value="اخري">أخرى</MenuItem>
                </Select>
              </FormControl>
            </Grid>

            <Grid item xs={12}>
              <TextareaAutosize
                minRows={4}
                style={{
                  width: "100%",
                  border: `2px solid ${
                    errors.description ? "#f00" : "#00e676"
                  }`,
                  borderRadius: 6,
                  padding: 12,
                }}
                placeholder="أدخل وصف المشكلة بالتفصيل"
                value={newTicket.description}
                onChange={(e) =>
                  setNewTicket({ ...newTicket, description: e.target.value })
                }
              />
            </Grid>

            <Grid item xs={12}>
              <Button component="label" variant="contained">
                اختر مرفق
                <input
                  hidden
                  type="file"
                  onChange={(e) => setFile(e.target.files[0])}
                />
              </Button>
              {file && <Typography sx={{ mt: 1 }}>{file.name}</Typography>}
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions sx={{display: "flex" , justifyContent:"space-between"}}>
          <Button onClick={() => setOpenTicketDialog(false)} sx={{ background: 'red', color: '#fff', fontWeight: 'bold', '&:hover': { background: 'red' } }}>إلغاء</Button>
          <Button onClick={handleAddTicket} disabled={loading} sx={{ background: '#00e676', color: '#fff', fontWeight: 'bold', '&:hover': { background: '#00c853' } }}>
            {loading ? <CircularProgress size={20} /> : "إرسال"}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Enhanced Filter Bar (centered below card) */}
      <Card
        sx={{
          mb: 4,
          p: 3,
          borderRadius: 4,
          boxShadow: "none",
          background: "#353535",
          border: "none",
          display: "flex",
          gap: 3,
          alignItems: "center",
          flexWrap: "wrap",
          justifyContent: "center",
        }}
        elevation={3}
      >
        <FormControl
          sx={{
            minWidth: 160,
            background: "#353535",
            borderRadius: 3,
            boxShadow: "none",
            mr: 1,
            "& .MuiInputLabel-root": {
              color: "#fff",
              fontWeight: "bold",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              fontWeight: "bold",
              fontSize: "1.1rem",
              "& fieldset": { borderColor: "#555" },
              "&:hover fieldset": { borderColor: "#888" },
              "&.Mui-focused fieldset": { borderColor: "#00e676" },
            },
          }}
        >
          <InputLabel id="status-filter-label">حالة التذكرة</InputLabel>
          <Select
            labelId="status-filter-label"
            value={filterStatus}
            label="حالة التذكرة"
            onChange={(e) => setFilterStatus(e.target.value)}
          >
            <MenuItem value="الكل">الكل</MenuItem>
            <MenuItem value="البداية">البداية</MenuItem>
            <MenuItem value="قيد التنفيذ">قيد التنفيذ</MenuItem>
            <MenuItem value="منتهية">منتهية</MenuItem>
          </Select>
        </FormControl>
        <FormControl
          sx={{
            minWidth: 180,
            background: "#353535",
            borderRadius: 3,
            boxShadow: "none",
            mr: 1,
            "& .MuiInputLabel-root": {
              color: "#fff",
              fontWeight: "bold",
            },
            "& .MuiOutlinedInput-root": {
              borderRadius: 3,
              fontWeight: "bold",
              fontSize: "1.1rem",
              "& fieldset": { borderColor: "#555" },
              "&:hover fieldset": { borderColor: "#888" },
              "&.Mui-focused fieldset": { borderColor: "#00e676" },
            },
          }}
        >
          <InputLabel id="title-filter-label">العنوان</InputLabel>
          <Select
            labelId="title-filter-label"
            value={filterTitle}
            label="العنوان"
            onChange={(e) => setFilterTitle(e.target.value)}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 2,
                  boxShadow: "none",
                  background: "#353535",
                  color: "#fff",
                },
              },
            }}
          >
            {uniqueTitles.map((title, idx) => (
              <MenuItem
                key={idx}
                value={title}
                sx={{
                  fontWeight: "bold",
                  color: title === "الكل" ? "#00e676" : "#fff",
                  "&.Mui-selected": { background: "#353535", color: "#00e676" },
                }}
              >
                {title}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <FormControl
          sx={{
            minWidth: 160,
            background: "#353535",
            borderRadius: 3,
            boxShadow: "none",
            mr: 1,
          }}
        >
          <InputLabel id="month-filter-label" sx={{ color: "#fff", fontWeight: "bold" }}>الشهر</InputLabel>
          <Select
            labelId="month-filter-label"
            value={filterMonth}
            label="الشهر"
            onChange={(e) => setFilterMonth(e.target.value)}
            sx={{
              borderRadius: 2,
              fontWeight: "bold",
              "&:hover": { borderColor: "#888" },
              "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
                borderColor: "#00e676",
              },
            }}
            MenuProps={{
              PaperProps: {
                sx: {
                  borderRadius: 2,
                  boxShadow: "none",
                  background: "#353535",
                  color: "#fff",
                },
              },
            }}
          >
            {monthsWithTickets.map((m) => (
              <MenuItem key={m} value={m} sx={{ color: '#fff', fontWeight: 'bold', '&.Mui-selected': { color: '#00e676' } }}>
                {dayjs(m + "-01").format("MMMM YYYY")}
              </MenuItem>
            ))}
          </Select>
        </FormControl>
        <Button
          variant="contained"
          sx={{
            background: "#00e676",
            color: "#fff",
            fontWeight: "bold",
            borderRadius: 2,
            ml: 2,
            px: 3,
            boxShadow: "none",
            '&:hover': { background: '#00c853' },
          }}
          onClick={() => {
            setFilterStatus("الكل");
            setFilterTitle("الكل");
            setFilterMonth(dayjs().format("YYYY-MM"));
          }}
        >
          إعادة تعيين الفلاتر
        </Button>
      </Card>

      {/* جدول التذاكر */}
      <Paper sx={{ mt: 4 }}>
        <TableContainer sx={{background: '#353535', borderRadius: 3, boxShadow: '0 4px 24px 0 rgba(56,142,60,0.10)' }}>
          <Table sx={{  minWidth: 900 }}>
            <TableHead>
              <TableRow
                sx={{
                  position: 'sticky',
                  top: 0,
                  background: '#353535',
                  zIndex: 1,
                  color: '#fff',
                }}
              >
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>#</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>العنوان</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>الوصف</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>الحالة</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>الرد</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>المرفق</TableCell>
                <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold', fontSize: '1.1rem' }}>التاريخ</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {allTickets
                .filter((t) => t.department === myDepartment)
                .filter((t) => filterStatus === 'الكل' || t.status === filterStatus)
                .filter((t) => filterTitle === 'الكل' || t.title === filterTitle)
                .filter((t) => t.created_at.startsWith(filterMonth)).length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={7} align="center">
                      <Typography color="text.secondary" sx={{ my: 3 }}>
                        لا يوجد تذاكر لعرضها
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  allTickets
                    .filter((t) => t.department === myDepartment)
                    .filter((t) => filterStatus === 'الكل' || t.status === filterStatus)
                    .filter((t) => filterTitle === 'الكل' || t.title === filterTitle)
                    .filter((t) => t.created_at.startsWith(filterMonth))
                    .map((t, i) => (
                      <TableRow
                        key={i}
                        sx={{
                          transition: 'all 0.2s cubic-bezier(.4,2,.6,1)',
                          cursor: 'pointer',
                          background: '#353535',
                          '&:hover': {
                            backgroundColor: '#444',
                            boxShadow: '0 4px 24px 0 rgba(0,255,100,0.10)',
                            transform: 'scale(1.01)',
                          },
                        }}
                      >
                        <TableCell align="center" sx={{ color: '#00e676', fontWeight: 'bold' }}>{i + 1}</TableCell>
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 500 }}>{t.title}</TableCell>
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 500 }}>{t.description}</TableCell>
                        <TableCell align="center">
                          <Box sx={{ backgroundColor: statusColors[t.status] || '#999', color: '#fff', borderRadius: 2, px: 2, fontWeight: 600 }}>{t.status}</Box>
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 500 }}>{t.answer || '-'}</TableCell>
                        <TableCell align="center">
                          {t.attachment ? (
                            t.attachment.match(/\.(jpg|jpeg|png|gif)$/i) ? (
                              <a
                                href={`https://backenderpforseg.samielmetwali.com/${t.attachment}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                style={{ display: 'inline-block' }}
                                aria-label="تحميل الصورة"
                              >
                                <img
                                  src={`https://backenderpforseg.samielmetwali.com/${t.attachment}`}
                                  alt="attachment"
                                  style={{ maxWidth: 60, maxHeight: 60, borderRadius: 6, cursor: 'pointer', border: '2px solid #00e676', marginBottom: 4 }}
                                />
                                <br />
                                <Button
                                  size="small"
                                  sx={{ mt: 1, color: '#00e676', fontWeight: 'bold', fontSize: '1rem' }}
                                  startIcon={<DownloadIcon sx={{ color: '#00e676', fontSize: 22 }} aria-label="تحميل" />}
                                >
                                  تحميل
                                </Button>
                              </a>
                            ) : (
                              <a
                                href={`https://backenderpforseg.samielmetwali.com/${t.attachment}`}
                                target="_blank"
                                rel="noopener noreferrer"
                                download
                                style={{ display: 'inline-flex', alignItems: 'center', gap: 4, color: '#2196f3', fontWeight: 'bold' }}
                                aria-label="تحميل الملف"
                              >
                                <DownloadIcon fontSize="large" sx={{ color: '#3d7fb6ff', fontSize: 22 }} aria-label="تحميل" />
                                ملف مرفق (تحميل)
                              </a>
                            )
                          ) : (
                            <span style={{ color: '#888' }}>-</span>
                          )}
                        </TableCell>
                        <TableCell align="center" sx={{ color: '#fff', fontWeight: 500 }}>{t.created_at}</TableCell>
                      </TableRow>
                    ))
                )}
            </TableBody>
          </Table>
        </TableContainer>
      </Paper>
    </Box>
  );
};

export default EmployeeTickets;
